import { useMutation, useQueryClient } from '@tanstack/react-query';

import { activateCustomer } from '#/api/crmMgmt/customer/activate';
import { deactivateCustomer } from '#/api/crmMgmt/customer/deactivate';
import {
  getCustomerApiErrorMessage,
  isCustomerVersionConflict,
} from '#/modules/crmMgmt/customer/helper/apiError';
import {
  customerDetailQueryKey,
  customerListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

/** 與 SideMsg／Detail CommandBar 文案一致。 */
export const CUSTOMER_ACTIVATE_CONFIRM = {
  title: '啟用客戶',
  message: '確定啟用此客戶？啟用後可被新客戶訂單選用。',
  confirmText: '啟用',
};

export const CUSTOMER_DEACTIVATE_CONFIRM = {
  title: '停用客戶',
  message: '確定停用此客戶？停用後仍可讀取，但不可被新客戶訂單選用。',
  confirmText: '停用',
};

/**
 * 客戶啟用／停用 mutation（confirm 由呼叫端處理）。
 *
 * @param {object} options
 * @param {string} options.id
 * @param {() => string|null|undefined} options.getUpdatedAt
 * @param {() => void|Promise<void>} [options.onAfterSuccess]
 * @param {() => void|Promise<void>} [options.onVersionConflict]
 */
function useCustomerStatusMutation({
  id,
  getUpdatedAt,
  onAfterSuccess,
  onVersionConflict,
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ action }) => {
      const payload = { updatedAt: getUpdatedAt?.() };
      if (action === 'activate') {
        return activateCustomer(id, payload);
      }
      if (action === 'deactivate') {
        return deactivateCustomer(id, payload);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result) => {
      toast.success(result?.message || '操作成功');
      await queryClient.invalidateQueries({ queryKey: customerListQueryKeyPrefix() });
      if (id) {
        await queryClient.invalidateQueries({ queryKey: customerDetailQueryKey(id) });
      }
      await onAfterSuccess?.(result);
    },
    onError: (error) => {
      toast.error(getCustomerApiErrorMessage(error));
      if (isCustomerVersionConflict(error)) {
        onVersionConflict?.(error);
      }
    },
  });
}

export default useCustomerStatusMutation;
